import { common as c, operators as o } from '@pendo/aggregations';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { EventName, FilterType } from '@/types/analytics/analytics.types';
import generateAggregationSegment from '@/data-explorer/aggregation-builder/segment-builder/segment-builder';
import TYPE_SPECIFIC_CONSTANTS from '@/data-explorer/aggregation-builder/constants/type-specific-constants-mapping';
import groupBy from 'lodash/groupBy';
import { DxEventType } from '@/types/analytics/data-explorer.types';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';

export default {
    name: 'sample-values',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.DATA_EXPLORER,
    responseFormat: 'row',
    build: ({
        filterType,
        kind,
        field,
        group,
        targetEntities,
        filterString,
        limit = 500,
    }: {
        filterType: FilterType;
        kind: string;
        field: string;
        group: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        targetEntities: any[];
        filterString?: string;
        limit?: number;
    }) => {
        const isAccount = filterType === FilterType.Account;
        const isParentAccount = filterType === FilterType.ParentAccount;
        const isVisitor = filterType === FilterType.Visitor;
        const isPageParameter = filterType === FilterType.PageParameter;

        const targetsByType = groupBy(targetEntities, 'type');
        const pipelines: c.PipelineData[][] = [];
        Object.entries(targetsByType).forEach(([key, value]) => {
            if (isPageParameter && key !== EventName.Page) {
                return;
            }

            const targetTypeName = key as EventName;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const targetsOfType = value as any[];
            const targetIds = targetsOfType.map(({ id }) => id);
            const targetType: DxEventType = TYPE_SPECIFIC_CONSTANTS[targetTypeName];
            const { sourceId, sourceEvents } = targetType;
            const isAnyActivity = targetTypeName === EventName.App;

            let appId;
            let eventSourceFilter;
            let postEventSourceFilter: c.PipelineData[] = [];

            if (isAnyActivity) {
                appId = targetsOfType.map(({ id }) => id);
                eventSourceFilter = { appId };
            } else if (targetTypeName === EventName.Guide) {
                const appIdsSet = new Set(targetsOfType.flatMap(({ appIds }) => appIds));
                eventSourceFilter = { appId: [...appIdsSet] };
                postEventSourceFilter = [
                    o.filter(targetsOfType.map(({ id }) => `${sourceId} == "${id}"`).join(' || ')),
                ];
            } else {
                appId = 'expandAppIds("*")';
                eventSourceFilter = { [`${sourceId}s`]: targetIds };
            }

            pipelines.push(
                o.pipeline(
                    o.sources[sourceEvents]({
                        ...eventSourceFilter,
                        timeSeries: {
                            first: 'dateAdd(now(), -7, "days")',
                            last: 'now()',
                            period: 'dayRange',
                        },
                        blacklist: 'apply',
                    }),
                    ...postEventSourceFilter,
                    ...generateAggregationSegment({
                        segmentId: 'everyone',
                        hasShowAnonymousFlag: hasSubscriptionFlag('showAnonymous'),
                    }),
                    isVisitor && o.bulkExpand('visitor'),
                    isAccount && o.bulkExpand('account'),
                    isParentAccount && o.bulkExpand('parentAccount'),
                    ...addEvalExpression(filterType, kind, group, field),
                    o.filter('sampleValue != "" && sampleValue != null'),
                    filterString !== undefined &&
                        o.filter(`contains(toLowerCase(sampleValue), "${filterString.toLowerCase()}")`),
                ),
            );
        });

        if (pipelines.length === 0) {
            return [];
        }

        if (pipelines.length > 1) {
            return o.pipeline(
                o.spawn(...pipelines),
                o.group(['sampleValue']),
                o.limit(limit),
                o.reduce({ sampleValues: o.list('sampleValue') }),
            );
        }

        const mainPipeline = pipelines[0];
        mainPipeline.push(o.group(['sampleValue']), o.limit(limit), o.reduce({ sampleValues: o.list('sampleValue') }));

        return mainPipeline;
    },
};

function addEvalExpression (filterType: FilterType, kind: string, group: string, field: string): c.PipelineData[] {
    if (filterType === FilterType.Visitor) {
        return [o.evalExpression('sampleValue', `visitor.${group}.${field}`)];
    }

    if (filterType === FilterType.Account) {
        return [o.evalExpression('sampleValue', `account.${group}.${field}`)];
    }

    if (filterType === FilterType.ParentAccount) {
        return [o.evalExpression('sampleValue', `parentAccount.${group}.${field}`)];
    }

    if (filterType === FilterType.Utm) {
        return [o.evalExpression('sampleValue', `properties.__utm__.${field}`)];
    }

    if (filterType === FilterType.Custom) {
        return [o.evalExpression('sampleValue', `properties.${field}`)];
    }

    if (filterType === FilterType.PageParameter) {
        return [o.evalExpression('sampleValue', 'parameters.parameter')];
    }
    if (filterType === FilterType.Location && field === 'region') {
        const countryRegionEval =
            'if(isNull(country)||country==""||isNull(region)||region=="", null, country+"-"+region)';

        return [o.evalExpression('sampleValue', countryRegionEval)];
    }
    if (filterType === FilterType.Location || filterType === FilterType.EventTopLevel) {
        return [o.evalExpression('sampleValue', field)];
    }

    if (kind === 'automatic') {
        return [o.userAgent('u', 'metadata.auto.lastuseragent'), o.evalExpression('sampleValue', `u.${field}`)];
    }

    if (filterType === FilterType.HistoricalMetadata) {
        const historicalMetadataField = `properties.__sg__.${kind.toLowerCase()}metadata.${group}__${field}`;

        return [
            o.evalExpression(
                'sampleValue',
                `if(isString(${historicalMetadataField}), trim(trim(${historicalMetadataField}, "\\n"), " "), ${historicalMetadataField})`,
            ),
        ];
    }

    return [o.evalExpression('sampleValue', `metadata.${group}.${field}`)];
}
